import { useCallback } from 'react'
import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { PREFER_CACHE } from '@constants/index'
import { InsurancePlan, InsurancePlanType } from '@typesApp/InsurancePlan'
import { getDecoratedInsurancePlans } from '@utils/benefitPlanUtil'
import { BenefitDetails, getBenefitPlanDetails } from '@utils/benefitPlanDetails'
import { useLazyGetInsurancePlansQuery } from '@redux/queries/claimRecords'
import { useClaimRecord } from '@hooks/useClaimRecord'
import { useEnvironment } from '@hooks/useEnvironment'
import { usePageFlow } from '@hooks/usePageFlow'
import { DateUtil } from '@utils/dateUtil'
import { CommerceBenefitPlanResponse } from '../types'

type AdjustedText = {
  benefitDescription: string
  copayInformation?: string
}

const EMPTY_RESPONSE = {} as CommerceBenefitPlanResponse

const UNAVAILABLE_RESPONSE = {
  status: 'unavailable',
} as CommerceBenefitPlanResponse

type UseBenefitPlanServiceResult = {
  getBenefitPlan: () => Promise<CommerceBenefitPlanResponse>
}

/**
 * @returns simplified benefit plan data for commerce F/E
 */
export const useBenefitPlanService = (): UseBenefitPlanServiceResult => {
  const { translationNamespace, dateFormat } = useEnvironment()
  const { t } = useTranslation(translationNamespace)
  const { benefitPlanConfiguration } = usePageFlow()
  const { claimRecordId } = useClaimRecord()
  const [triggerFetchPlans] = useLazyGetInsurancePlansQuery()

  const getBenefitPlan = useCallback(async (): Promise<CommerceBenefitPlanResponse> => {
    const adjustBenefitText = (details: BenefitDetails, displayPlanType: InsurancePlanType): AdjustedText => {
      const {
        serviceDescription,
        isComingSoon,
        isRenewal,
        isCopay,
        isCustomerServiceOnly,
        showFallbackDescription,
        benefitItem,
      } = details

      let benefitDescription = serviceDescription

      if (isComingSoon) {
        benefitDescription = t(`InsurancePlans.benefits-coming-soon.${displayPlanType}`)
      } else if (isRenewal && benefitItem?.benefitRenewalDate) {
        benefitDescription = t('InsurancePlans.benefit-renewal-unformatted', {
          date: DateUtil.fromISODate(benefitItem.benefitRenewalDate, dateFormat),
        })
      }

      if (displayPlanType === 'FRAME' && showFallbackDescription) {
        benefitDescription = t('InsurancePlans.service-description.frame')
      }

      if (displayPlanType === 'LENSES' && showFallbackDescription) {
        benefitDescription = t('InsurancePlans.service-description.lenses')
      }

      if (displayPlanType === 'FRAME_WITH_LENSES') {
        let translationKey = 'InsurancePlans.additional-purchase-benefits-callout.frame-with-lenses'
        if (isCustomerServiceOnly) {
          translationKey = `${translationKey}-customer-service-only-unformatted`
        }
        benefitDescription = t(translationKey)
      }

      const copayInformation = isCopay ? t('InsurancePlans.copay-conditions') : undefined

      return {
        benefitDescription: benefitDescription ?? '',
        copayInformation,
      }
    }

    const transformResponse = (plans: InsurancePlan[]): CommerceBenefitPlanResponse => {
      const decoratedPlans = getDecoratedInsurancePlans(plans, benefitPlanConfiguration?.displayOrder)

      const benefits: CommerceBenefitPlanResponse['benefits'] = decoratedPlans.map(
        ({ plan, displayPlanType, variant }) => {
          const details = getBenefitPlanDetails(plan, variant)
          const { benefitDescription, copayInformation } = adjustBenefitText(details, displayPlanType)

          return {
            // note: in case of additional-purchase variant, the display plan type
            // is FRAME_WITH_LENSES, but the actual plan is FRAME or LENSES. We return
            // the display plan type to client to allow it to differentiate between
            // routine FRAME or LENSES benefits.
            planType: displayPlanType,
            benefitStatus: details?.benefitItem?.benefitStatus ?? 'BENEFITS_NOT_AVAILABLE',
            benefitAvailability: details?.benefitItem?.benefitAvailability ?? 'NOT_SUPPORTED',
            benefitDescription,
            copayInformation,
          }
        }
      )

      return {
        status: benefits.length ? 'available' : 'unavailable',
        benefits,
      }
    }

    if (!claimRecordId) {
      return UNAVAILABLE_RESPONSE
    }

    try {
      const plans = await triggerFetchPlans({ claimRecordId }, PREFER_CACHE).unwrap()
      return transformResponse(plans)
    } catch (error) {
      return EMPTY_RESPONSE
    }
  }, [triggerFetchPlans, benefitPlanConfiguration?.displayOrder, claimRecordId, dateFormat, t])

  return {
    getBenefitPlan,
  }
}
